import { ApolloLink, CombinedGraphQLErrors } from '@apollo/client';
import { ErrorLink } from '@apollo/client/link/error';
import { datadogRum } from '@datadog/browser-rum';
import { PrinceSdkProvider } from '@teamexos/prince-sdk';
import fastRedact from 'fast-redact';
import React from 'react';

import { clientOptions } from '../apollo/client';

const redactObjects = {
  paths: [
    '*.email',
    '*.photo',
    '*.avatar',
    '*.cronofyElementToken',
    '*.firstName',
    '*.lastName',
    '*.name',
    '*.userName',
    '*.messages',
    '*.text',
    '*.photos',
    '*.note',
    '*.signedUrl',
    '*.birthday',
    '*.pronouns',
    '*.age',
    '*.profileJson',
    '*.token',
    '*.iterableToken',
    '*.additionalInfo',
    '*.senderName',
    '*.coachFirstName',
    '*.coachAssignmentFirstName',
    '*.coachAssignmentLastName',
    '*.content',
    'email',
    'photo',
    'avatar',
    'cronofyElementToken',
    'firstName',
    'lastName',
    'name',
    'userName',
    'messages',
    'text',
    'photos',
    'note',
    'signedUrl',
    'birthday',
    'pronouns',
    'age',
    'profileJson',
    'token',
    'iterableToken',
    'additionalInfo',
    'senderName',
    'coachFirstName',
    'coachAssignmentFirstName',
    'coachAssignmentLastName',
    'content',
  ],
};

const redact = fastRedact(redactObjects);

const errorLink = new ErrorLink(({ error, operation }) => {
  if (CombinedGraphQLErrors.is(error)) {
    error.errors.forEach((graphQLError) => {
      datadogRum.addError(new Error(graphQLError.message), {
        operationName: operation.operationName,
        releaseSHA: process.env.GATSBY_COMMIT_ID,
        variables: redact(operation.variables),
        extensions: operation.extensions,
        apolloGraphQLError: {
          path: graphQLError.path,
          message: graphQLError.message,
          extensions: graphQLError.extensions,
        },
      });
    });
    return;
  }

  if (error.stack?.includes('hammerhead.js')) {
    // filter testcafe e2e noise
    return;
  }
  datadogRum.addError(new Error(error.message), {
    operationName: operation.operationName,
    releaseSHA: process.env.GATSBY_COMMIT_ID,
    variables: redact(operation.variables),
    extensions: operation.extensions,
    apolloNetworkError: {
      error,
      extensions: (error as { extensions?: unknown }).extensions,
    },
  });
});

const PrinceSDKProvider: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const headersLink = new ApolloLink((operation, forward) => {
    operation.setContext({
      headers: {
        'x-application-version': `web/${process.env.GATSBY_COMMIT_ID}`,
        'x-application-name': 'shared-platform-admin',
        'x-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    });
    return forward(operation);
  });

  const link = ApolloLink.from([headersLink, errorLink]);

  return (
    <PrinceSdkProvider
      clientOptions={{
        ...clientOptions,
        extraLink: link,
      }}
    >
      {children}
    </PrinceSdkProvider>
  );
};

export default PrinceSDKProvider;
